import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { AppetiteType as Appetite_Type_Enum } from '@risksmart-app/domain/src/types/consts/appetite-type';
import _ from 'lodash';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LatestRatingsPreview from 'src/components/latest-ratings-preview';
import type { ResultProps } from 'src/components/latest-ratings-preview/LatestRatingsPreview';
import ImpactRatingModal from 'src/pages/impacts/ratings/ImpactRatingModal';
import { getPerformanceRatingFromRatingAndAppetite } from 'src/pages/impacts/ratings/performanceCalculation';
import { notEmpty } from 'src/utilityTypes';

import {
  useGetActiveAppetitesByParentId,
  useGetLatestImpactRatingsForRatedImpactsByRatedItemId,
} from '@/hooks/queries';

type Props = {
  riskId: string;
};

const DetailsAside: FC<Props> = ({ riskId }) => {
  const { t } = useTranslation('common');

  const [selectedImpactRatingId, setSelectedImpactRatingId] = useState<
    string | undefined
  >();
  const [showImpactRatingModal, setShowImpactRatingModal] =
    useState<boolean>(false);

  const { data: impactRatingResults } =
    useGetLatestImpactRatingsForRatedImpactsByRatedItemId({
      queryArgs: { ratedItemId: riskId },
    });

  const { data: appetiteData } = useGetActiveAppetitesByParentId({
    queryArgs: { parentId: riskId },
  });

  const activeImpactAppetites = appetiteData?.appetite_parent
    .map((ap) => ap.appetite)
    .filter(notEmpty)
    .filter((a) => a?.AppetiteType === Appetite_Type_Enum.Impact);

  const impactRatingsThatHavePerformance = impactRatingResults?.impact
    .filter((c) => c.ratings.length > 0)
    .map((c) => ({
      ...c,
      performanceRating: getPerformanceRatingFromRatingAndAppetite({
        rating: c.ratings[0].Rating,
        impactAppetite: activeImpactAppetites?.find(
          (a) => a.impact?.Id === c.ratings[0].ImpactId
        )?.ImpactAppetite,
      }),
    }))
    .filter((c) => !_.isNil(c.performanceRating));

  const firstImpactWithRating = impactRatingResults?.impact.find(
    (c) => c.ratings.length > 0
  );

  const showLikelihoodPreview = !!firstImpactWithRating;
  const showImpactCategoriesPreview =
    !!impactRatingsThatHavePerformance &&
    impactRatingsThatHavePerformance.length > 0;
  const showImpactRatings =
    showLikelihoodPreview || showImpactCategoriesPreview;

  if (!showImpactRatings) {
    return null;
  }

  return (
    <>
      <SpaceBetween size={'m'}>
        {showLikelihoodPreview && firstImpactWithRating && (
          <LatestRatingsPreview
            ratingsTitle={''}
            assessmentResults={[
              {
                id: firstImpactWithRating.ratings[0].Id,
                title: t('impactRatings.fields.Likelihood'),
                rating: firstImpactWithRating.ratings[0].Likelihood,
                ratingType: 'likelihood',
                completionDate: firstImpactWithRating.ratings[0].TestDate,
              },
            ]}
            onClick={(id) => {
              setSelectedImpactRatingId(id);
              setShowImpactRatingModal(true);
            }}
          />
        )}
        {showImpactCategoriesPreview && (
          <LatestRatingsPreview
            ratingsTitle={t('impacts.tab_title')}
            assessmentResults={impactRatingsThatHavePerformance!.map<ResultProps>(
              (c) => ({
                id: c.ratings[0].Id,
                title: c.Name,
                rating: c.performanceRating!,
                ratingType: 'impact_performance_rating',
                completionDate: c.ratings[0].TestDate,
              })
            )}
            onClick={(id) => {
              setSelectedImpactRatingId(id);
              setShowImpactRatingModal(true);
            }}
          />
        )}
      </SpaceBetween>
      {showImpactRatingModal && (
        <ImpactRatingModal
          impactRatingId={selectedImpactRatingId}
          onDismiss={() => setShowImpactRatingModal(false)}
          onSaving={() => Promise.resolve()}
        />
      )}
    </>
  );
};

export default DetailsAside;
